import { JsonResponse, RequestConfig, SupersetClient } from '@aibi-ui/core';
import { InteractInputItem } from 'src/conversation/components/ProcessStep/types';
import { BasePageListApiQuery } from 'src/types/base';
import {
  ChatJustify,
  ConversationParams,
  ShareConversationInfo,
} from 'src/types/conversation';
import { getCryptoHeaders } from 'src/utils/anonymous';

/** 获取加密参数 */
export const getChatSecurityInit = async () =>
  SupersetClient.get({
    endpoint: '/api/v1/anonymous/chat/security/init',
  });

/** 匿名请求 */
export const makeAnonymousRequest = async (requestConfig: RequestConfig) => {
  const {
    json: { result: data },
  } = await getChatSecurityInit();
  const headers = getCryptoHeaders(data);
  return SupersetClient.request({ ...requestConfig, headers });
};

/** 提问 */
export const postChatConversation = (
  jsonPayload: Omit<ConversationParams, 'user_id'> & { telant_id: string },
) =>
  makeAnonymousRequest({
    endpoint: '/api/v1/anonymous/chat/conversation',
    method: 'POST',
    jsonPayload,
  });

/** 停止对话 */
export const postChatConversationStop = (jsonPayload: {
  conversation_id: string;
  round_id: string;
  tenant_id: string;
}) =>
  makeAnonymousRequest({
    endpoint: '/api/v1/anonymous/chat/conversation/stop',
    method: 'POST',
    jsonPayload,
  });

/** 获取对话记录 */
export const getChatRecord = (
  searchParams: BasePageListApiQuery & {
    title?: string;
    topic_id?: number | string;
    tenant_id: string;
  },
) =>
  makeAnonymousRequest({
    endpoint: '/api/v1/anonymous/chat/record',
    method: 'GET',
    searchParams,
  });

/** 获取对话详情 */
export const getChatDetail = async (searchParams: {
  conversation_id: string;
  tenant_id: string;
}) =>
  makeAnonymousRequest({
    endpoint: '/api/v1/anonymous/chat/detail',
    method: 'GET',
    searchParams,
  });

/**
 * 选择实体
 */
export const postChatInteract = (jsonPayload: {
  conversation_id: string;
  round_id: string;
  content: InteractInputItem[] | null;
}) =>
  makeAnonymousRequest({
    endpoint: '/api/v1/anonymous/chat/interact',
    method: 'POST',
    jsonPayload,
  });

/** 获取佐证数据 */
export const getChatJustifyData = (
  searchParams: ChatJustify.ListQuery & { tenant_id: string },
): Promise<JsonResponse<{ result: ChatJustify.DataItem[] }>> =>
  makeAnonymousRequest({
    endpoint: '/api/v1/anonymous/chat/justify/data',
    method: 'GET',
    searchParams,
  });

/** 获取指定数据下钻结果 */
export const postChatJustifyDrill = (
  jsonPayload: ChatJustify.DrillQuery & { tenant_id: string },
): Promise<JsonResponse<{ result: ChatJustify.DrillData }>> =>
  makeAnonymousRequest({
    endpoint: '/api/v1/anonymous/chat/justify/drill',
    method: 'POST',
    jsonPayload,
  });

/** 更新对话名称 */
export const putChatRecordStatus = (jsonPayload: {
  id: string;
  title: string;
}) =>
  makeAnonymousRequest({
    endpoint: '/api/v1/anonymous/chat/record/status',
    method: 'PUT',
    jsonPayload,
  });

/** 删除对话 */
export const deleteChatRecordById = (
  id: string,
  jsonPayload: { tenant_id: string },
) =>
  makeAnonymousRequest({
    endpoint: `/api/v1/anonymous/chat/record/${id}`,
    method: 'DELETE',
    jsonPayload,
  });

/** 分享对话 */
export const postChatShare = (jsonPayload: ShareConversationInfo) =>
  makeAnonymousRequest({
    endpoint: '/api/v1/anonymous/chat/share',
    method: 'POST',
    jsonPayload,
  });

/** 根据short_code查询主题 */
export const getChatByShortCode = (short_code: string) =>
  makeAnonymousRequest({
    endpoint: `/api/v1/anonymous/chat/by-short-code/${short_code}`,
    method: 'GET',
  });
